<template>
  <div class="kpi-card" @click="handleClick">
    <!-- Action Buttons -->
    <div class="card-actions">
      <b-button size="sm"
                :disabled="true"
                class="card-action-btn"
                :title="$t('Webportal.Edit')"
                @click.stop="$emit('edit-kpi', kpi)">
        <MoreVertical :size="12" />
      </b-button>
      <b-button size="sm"
                :disabled="true"
                class="card-action-btn card-action-btn-danger"
                :title="$t('Webportal.Delete')"
                @click.stop="$emit('delete-kpi', kpi)">
        <Trash2 :size="12" />
      </b-button>
    </div>

    <!-- Badges -->
    <div class="card-badges">
      <span class="card-badge">{{ KPI.country(kpi) }}</span>
      <span class="badge-separator">•</span>
      <span class="card-badge">{{ KPI.wave(kpi) }}</span>
      <span class="badge-separator">•</span>
      <span class="card-badge">{{ kpi.type }}</span>
    </div>

    <!-- Header -->
    <div class="card-header">
      <div class="card-header-left">
        <p class="card-category no-break">{{ kpi.Name }}</p>
        <p class="card-title no-break">{{ kpi.kpiCode }}</p>
      </div>
      <div class="card-trend">
        <Minus :size="14" />
        <span>{{ formatTrend(kpi.trend) }}</span>
      </div>
    </div>

    <!-- Value & Target -->
    <div class="card-value">
      <span class="value-current">{{ KPI.lastResult(kpi) }}</span>
      <span class="value-target">/ {{ KPI.lastResultTarget(kpi) }} {{ $t('Webportal.Target') }}</span>
    </div>

    <!-- Footer -->
    <div class="card-footer">
      <span class="footer-text">
        {{ $t('Webportal.Prewave') }}: {{ KPI.secondLastResult(kpi) }}
      </span>
      <div :class="['status-indicator', `status-${getStatusColor(kpi)}`]" />
    </div>
  </div>
</template>

<script>
import { MoreVertical, Trash2, Minus } from 'lucide-vue-next';
import { useKpiHelpers } from '@/composables/kpi-helpers';

export default {
  name: 'KpiCard',

  components: {
    MoreVertical,
    Trash2,
    Minus
  },

  props: {
    kpi: {
      type: Object,
      required: true
    }
  },

  setup() {
    const KPI = useKpiHelpers();
    return { KPI };
  },

  emits: ['click-kpi', 'edit-kpi', 'delete-kpi'],

  methods: {
    // getValue(kpi) {
    //   const k = this.KPI.lastWave(kpi);
    //   return k.Result != null ? this.formatValue(k.Result) : '-';
    // },

    handleClick() {
      this.$emit('click-kpi', this.kpi);
    },

    formatValue(value) {
      if (value == null) return '-';
      return `${value}%`;
    },

    formatTrend(trend) {
      if (trend == null || trend === 0) return '0pp';
      const sign = trend > 0 ? '+' : '';
      return `${sign}${trend}pp`;
    },

    getStatusColor(kpi) {
      if (!kpi.value || !kpi.target) return 'gray';

      const ratio = kpi.value / kpi.target;
      if (ratio >= 1) return 'green';
      if (ratio >= 0.95) return 'yellow';
      return 'red';
    }
  }
}
</script>

<style lang="scss" scoped>
.kpi-card {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 15rem;
  gap: 12px;
  padding: 16px;
  background: white;
  border: 1px solid var(--color-border);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
                0 2px 4px -1px rgba(0, 0, 0, 0.06);
    border-color: rgba(2, 54, 112, 0.3);

    .card-actions {
      opacity: 1;
    }
  }
}

.card-actions {
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 10;
}

.card-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: white;
  border: 1px solid red;
  border-radius: 4px;
  color: red;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:hover {
    background: #f9fafb;
    color: #374151;
  }

  &-danger:hover {
    background: #fef2f2;
    border-color: #fecaca;
    color: #dc2626;
  }
}

.card-badges {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.card-badge {
  display: inline-flex;
  text-transform: uppercase;
  align-items: center;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 500;
  background: #f9fafb;
  color: var(--color-muted-foreground);
  border: 1px solid var(--color-border);
  border-radius: 4px;
}

.badge-separator {
  font-size: 12px;
  color: #d1d5db;
}

.card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  background-color: unset !important;
  padding: unset;
}

.card-header-left {
  flex: 1;
  min-width: 0;
}

.card-category {
  font-size: 12px;
  color: var(--color-muted-foreground);
  margin: 0 0 2px;
}

.card-title {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-foreground);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.no-break {
  white-space: nowrap;      /* kein Zeilenumbruch */
  overflow: hidden;         /* Überlauf abschneiden */
  text-overflow: ellipsis;  /* ... am Ende */
}
.card-trend {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--color-muted-foreground);
  white-space: nowrap;
}

.card-value {
  display: flex;
  align-items: baseline;
  gap: 8px;
  // margin: 4px 0;
}

.value-current {
  font-size: 24px;
  font-weight: 700;
  color: var(--target-primary);
}

.value-target {
  font-size: 12px;
  color: var(--color-muted-foreground);
}

.card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.footer-text {
  font-size: 12px;
  color: var(--color-muted-foreground);
}

.status-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;

  &.status-green {
    background: var(--default-status-green);
  }

  &.status-yellow {
    background: var(--default-status-yellow);
  }

  &.status-red {
    background: var(--default-status-red);
  }

  &.status-gray {
    background: var(--default-status-indifferent);
  }
}

// Dark Mode
:root.dark,
[data-bs-theme="dark"] {
  .kpi-card {
    background: var(--color-card);
    border-color: var(--color-border);
  }

  .card-action-btn {
    background: var(--color-card);
    border-color: var(--color-border);
    color: var(--color-muted-foreground);

    &:hover {
      background: var(--color-muted);
      color: var(--color-foreground);
    }
  }

  .card-badge {
    background: var(--color-muted);
    color: var(--color-foreground);
    border-color: var(--color-border);
  }

  .card-category,
  .card-trend,
  .value-target,
  .footer-text {
    color: var(--color-muted-foreground);
  }

  .card-title {
    color: var(--color-foreground);
  }

  .value-current {
    color: var(--color-primary);
  }
}
</style>
